import type { TransitPathLineStyle } from "../routeLines";
import type { DimmedStopLabelPresentation } from "../stopLabels";

/**
 * Paint and marker presentation constants for the realtime transit path overlay.
 */

/** Dimmed line opacity for traversed route and past stop markers. */
export const REALTIME_TRANSIT_PATH_LINE_OPACITY_DIMMED = 0.6;

/** Full line opacity for active current and remaining route segments. */
export const REALTIME_TRANSIT_PATH_LINE_OPACITY_ACTIVE = 1;

/** Line width for traversed (past) route segments. */
export const REALTIME_TRANSIT_PATH_LINE_WIDTH_DIMMED = 3;

/** Line width for active current and remaining route segments. */
export const REALTIME_TRANSIT_PATH_LINE_WIDTH_ACTIVE = 5;

/** Dash pattern for dimmed past route polylines. */
export const REALTIME_TRANSIT_PATH_LINE_DASHARRAY_DIMMED: [number, number] = [ 1.5, 1.5 ];

/** Marker opacity for future stops. */
export const REALTIME_TRANSIT_PATH_MARKER_OPACITY_ACTIVE = 1;

/**
 * How many shape points before the previous stop anchor the vehicle may snap backward
 * when projecting its live position onto the trip polyline.
 */
export const REALTIME_TRANSIT_PATH_SHAPE_SNAP_BACKWARD_TOLERANCE_POINTS = 15;

/** Marker opacity for past stop dots and labels. */
export const REALTIME_TRANSIT_PATH_MARKER_OPACITY_DIMMED = 0.75;

/** CSS saturate filter strength for dimmed past stop markers. */
export const REALTIME_TRANSIT_PATH_MARKER_SATURATE_DIMMED = 0.45;

/** CSS scale applied to dimmed past stop dots. */
export const REALTIME_TRANSIT_PATH_MARKER_DOT_SCALE_DIMMED = 0.82;

/** Dimmed marker colors and shadows for past stops on the realtime transit path. */
export const REALTIME_TRANSIT_PATH_DIMMED_MARKER_PRESENTATION: DimmedStopLabelPresentation = {
    "dotBorderColorDark":    "rgba(148, 163, 184, 0.75)",
    "dotBorderColorLight":   "rgba(100, 116, 139, 0.65)",
    "dotBoxShadowDark":      "0 0 0 1px rgba(255,255,255,0.2), 0 2px 5px rgba(0,0,0,0.25)",
    "dotBoxShadowLight":     "0 1px 4px rgba(15,23,42,0.18)",
    "labelTextColorDark":    "rgba(248, 250, 252, 0.9)",
    "labelTextColorLight":   "rgba(15, 23, 42, 0.78)",
    "labelBackgroundDark":   "rgba(15, 23, 42, 0.82)",
    "labelBackgroundLight":  "rgba(255, 255, 255, 0.94)",
    "labelBorderColorDark":  "rgba(148, 163, 184, 0.75)",
    "labelBorderColorLight": "rgba(100, 116, 139, 0.6)",
    "dotScale":              REALTIME_TRANSIT_PATH_MARKER_DOT_SCALE_DIMMED,
};

/**
 * Stable fingerprint of realtime transit path marker presentation constants.
 *
 * @returns Fingerprint string for cache invalidation.
 */
export function buildRealtimeTransitPathMarkerPresentationFingerprint(): string {
    const presentation = REALTIME_TRANSIT_PATH_DIMMED_MARKER_PRESENTATION;

    return [
        REALTIME_TRANSIT_PATH_MARKER_OPACITY_DIMMED,
        REALTIME_TRANSIT_PATH_MARKER_SATURATE_DIMMED,
        REALTIME_TRANSIT_PATH_MARKER_DOT_SCALE_DIMMED,
        presentation.dotBorderColorDark,
        presentation.dotBorderColorLight,
        presentation.dotBoxShadowDark,
        presentation.dotBoxShadowLight,
        presentation.labelTextColorDark,
        presentation.labelTextColorLight,
        presentation.labelBackgroundDark,
        presentation.labelBackgroundLight,
        presentation.labelBorderColorDark,
        presentation.labelBorderColorLight,
    ].join( "|" );
}

/** Default paint style for active current and remaining segments. */
export const REALTIME_TRANSIT_PATH_LINE_STYLE_ACTIVE: TransitPathLineStyle = {
    "lineOpacity": REALTIME_TRANSIT_PATH_LINE_OPACITY_ACTIVE,
    "lineWidth":   REALTIME_TRANSIT_PATH_LINE_WIDTH_ACTIVE,
};

/** Paint style for traversed (past) route segments. */
export const REALTIME_TRANSIT_PATH_LINE_STYLE_TRAVERSED: TransitPathLineStyle = {
    "lineOpacity":   REALTIME_TRANSIT_PATH_LINE_OPACITY_DIMMED,
    "lineWidth":     REALTIME_TRANSIT_PATH_LINE_WIDTH_DIMMED,
    "lineDasharray": REALTIME_TRANSIT_PATH_LINE_DASHARRAY_DIMMED,
};
